<script setup lang="ts">
/**
 * Thin presentation wrapper for a sentence-group autocomplete with category grouping.
 * Accepts raw SentenceGroupType[], groups them by category, and renders the
 * autocomplete with the correct item-props/title/value bindings.
 *
 * Category headers are rendered as bold, non-faded subheaders.
 * All other v-autocomplete props (multiple, chips, clearable, label, etc.)
 * are passed through via $attrs.
 */
import { SentenceGroupType } from 'types/types'
import { computed } from 'vue'

import {
  groupedItemProps,
  groupedItemTitle,
  groupedItemValue,
  groupSentenceGroupsByCategory,
  isCategoryHeader,
} from '@/util/sentenceGroupCategoryGrouping'

interface Props {
  sentenceGroups: ReadonlyArray<SentenceGroupType>
}

const props = defineProps<Props>()

const groupedItems = computed(() => groupSentenceGroupsByCategory(props.sentenceGroups))

defineOptions({ inheritAttrs: false })
</script>

<template>
  <v-autocomplete
    v-bind="$attrs"
    :item-props="groupedItemProps"
    :item-title="groupedItemTitle"
    :item-value="groupedItemValue"
    :items="groupedItems"
    return-object
    variant="underlined"
  >
    <template #item="{ item, props: slotProps }">
      <v-list-subheader v-if="isCategoryHeader(item.raw)" class="category-header" v-bind="slotProps">
        {{ item.raw._categoryDescription }}
      </v-list-subheader>
      <v-list-item v-else v-bind="slotProps" />
    </template>
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot v-if="name !== 'item'" :name="name" v-bind="slotData ?? {}" />
    </template>
  </v-autocomplete>
</template>

<style scoped>
.category-header {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
  color: rgba(var(--v-theme-on-surface), 0.87);
  font-weight: 700;
}
</style>
